import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { ApplicationState } from '~/Store';

import { useAvailabilityCreateFolder } from './adapters/useAvailabilityCreateFolder';
import { useAvailabilityData } from './adapters/useAvailabilityData';
import { AvailabilityList } from './components/AvailabilityList';
import { CreateFolderButton } from './components/CreateFolderButton';
import { CreateFolderDialog } from './components/CreateFolderDialog';
import { ExportButton } from './components/ExportButton';
import { Filters, FiltersValue } from './components/Filters';
import { Header } from './components/Header';
import { IntroSection } from './components/IntroSection';
import { DDD_SHARE_THRESHOLD, EXPORT_LIMIT } from './constants';
import { useAvailabilityExportSelection } from './hooks/useAvailabilityExportSelection';
import { useAvailabilityFilters } from './hooks/useAvailabilityFilters';
import { buildAvailabilityParams } from './utils/availabilityQueryParams';

const AVAILABILITY_BREADCRUMBS = [{ name: 'Dostupnosti' }];

export function AvailabilityPage() {
	const canCreateFolder = useSelector(
		(state: ApplicationState) =>
			state.accessToken.accessToken?.subscription?.systemModules.has('product-groups') ?? false
	);

	const { filters, page, perPage, changeFilters, changePage, changePerPage, resolveLabels } = useAvailabilityFilters();
	const createFolder = useAvailabilityCreateFolder(filters);

	const { data, isFetching, isError } = useAvailabilityData(buildAvailabilityParams(filters, page, perPage));

	const total = data?.pagination.total ?? 0;
	const selection = useAvailabilityExportSelection({ filters, total });

	useEffect(() => {
		if (data) {
			resolveLabels(data.items);
		}
		// resolveLabels only reads the loaded groups; re-run when a new page of data arrives.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	const handleFiltersChange = (value: FiltersValue) => {
		changeFilters(value);
	};

	const hasResults = !isError && !!data && data.items.length > 0;
	const isExportDisabled =
		!hasResults ||
		isFetching ||
		selection.exceedsLimitWithoutFilter ||
		(selection.showSelection && selection.selectedIds.size === 0);

	return (
		<div className='w-full h-full bg-white overflow-auto'>
			<Header breadcrumbs={AVAILABILITY_BREADCRUMBS} title='Monitor dostupnosti' />

			<IntroSection />

			<div className='w-full bg-gray-100 mb-6'>
				<div className='sticky top-0 z-20 w-full bg-gray-100 border-b border-gray-200'>
					<div className='px-10 py-4 max-w-[1260px] mx-auto space-y-4'>
						<Filters
							value={filters}
							onChange={handleFiltersChange}
							rightSlot={
								<>
									<ExportButton
										isExporting={selection.isExporting}
										disabled={isExportDisabled}
										onClick={selection.handleExport}
									/>
									{canCreateFolder ? (
										<CreateFolderButton
											disabled={!createFolder.hasActiveFilter}
											isCreating={createFolder.isCreating}
											onClick={createFolder.openCreateFolderDialog}
										/>
									) : null}
								</>
							}
						/>

						{selection.showSelection && hasResults ? (
							<p className='text-sm text-gray-500 -mt-4'>
								Exportovat lze maximálně {EXPORT_LIMIT} záznamů. Vybráno: {selection.selectedIds.size}/{EXPORT_LIMIT}.
							</p>
						) : null}
					</div>
				</div>

				<div className='px-10 pt-4 pb-4 max-w-[1260px] mx-auto'>
					<AvailabilityList
						data={data}
						isFetching={isFetching}
						isError={isError}
						dddShareThreshold={DDD_SHARE_THRESHOLD}
						selectable={selection.showSelection}
						selectedIds={selection.selectedIds}
						selectionDisabled={selection.selectedIds.size >= EXPORT_LIMIT}
						onToggleSelect={(group, checked) => selection.toggleSelect(group.id, checked)}
						perPage={perPage}
						onPageChange={changePage}
						onPerPageChange={changePerPage}
					/>
				</div>
			</div>

			{createFolder.isDialogOpen ? (
				<CreateFolderDialog
					filters={filters}
					isCreating={createFolder.isCreating}
					onClose={createFolder.closeCreateFolderDialog}
					onSubmit={createFolder.handleCreateFolder}
				/>
			) : null}
		</div>
	);
}
